import { useState } from 'react';
import {
  Brand,
  Button,
  Field,
  Icon,
  Input,
  Link,
  Modal,
  Notice,
  Panel,
  Csel,
  Sheet,
  Accordion,
  AccordionItem,
  ThemeToggle,
  Toggle,
  useToast,
  Reason,
} from '@efira/ui';

/**
 * Витрина состояний компонентов @efira/ui.
 *
 * Пришла на смену статической странице `components-preview.html`: с переходом
 * на Tailwind классы компилируются, и файл, открываемый двойным кликом,
 * компоненты показать не может. Маршрут публичный и без сессии - по нему
 * проходит автоматический аудит доступности (`pnpm a11y`) в обеих темах.
 * Dashboard целиком закрыт от индексации, витрина - тоже.
 */
export function ShowcaseScreen() {
  const toast = useToast();
  const [on, setOn] = useState(true);
  const [modal, setModal] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [zone, setZone] = useState('Europe/Moscow');

  return (
    <div className="bg-muted/40 min-h-dvh">
      <header className="bg-background border-foreground/10 border-b">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <Brand />
          <ThemeToggle />
        </div>
      </header>

      <main className="mx-auto flex max-w-5xl flex-col gap-6 px-4 py-6">
        <h1 className="text-2xl font-semibold">Витрина компонентов</h1>

        <Panel title="Кнопки" subtitle="Роль действия выбирает вариант">
          <div className="flex flex-wrap gap-2">
            <Button variant="primary">Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="danger">Danger</Button>
            <Button variant="danger-solid">Danger solid</Button>
            <Button variant="primary" loading>
              Загрузка
            </Button>
            <Button variant="primary" disabled aria-describedby="showcase-why">
              Недоступно
            </Button>
            <Reason id="showcase-why">
              Заблокированная главная рисуется вторичной, причина — рядом.
            </Reason>
            <Button variant="secondary" icon={<Icon name="copy" />}>
              С иконкой
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="primary" size="l">
              Большая
            </Button>
            <Button variant="primary" size="m">
              Средняя
            </Button>
            <Button variant="primary" size="s">
              Малая
            </Button>
          </div>
        </Panel>

        <Panel title="Поля" subtitle="Подсказка, ошибка, пароль с переключателем">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Почта" hint="На неё придёт ссылка" required>
              {(props) => <Input {...props} type="email" placeholder="you@example.com" />}
            </Field>
            <Field label="Пароль" error="Не короче 8 символов" required>
              {(props) => <Input {...props} type="password" defaultValue="1234" />}
            </Field>
            <Field label="Недоступное поле" hint="Доступно, когда включён «Бесконечный режим»">
              {(props) => <Input {...props} disabled defaultValue="60" />}
            </Field>
            <Field label="Поиск">
              {(props) => <Input {...props} type="search" placeholder="Подарок или цена" />}
            </Field>
          </div>
          <Toggle
            checked={on}
            onChange={setOn}
            label="Запомнить меня"
            hint="Сессия сохранится на этом устройстве"
          />
        </Panel>

        <Panel title="Сообщения" subtitle="Уровень формы или экрана">
          <Notice tone="info" title="Сначала выберите сцену">
            Привязки принадлежат игре: пока сцена не выбрана, привязывать нечего.
          </Notice>
          <Notice tone="success" title="Письмо отправлено">
            Откройте письмо и перейдите по ссылке.
          </Notice>
          <Notice tone="warning" title="Ссылка секретная">
            Она даёт доступ к вашему оверлею.
          </Notice>
          <Notice tone="error" title="Не получилось" live>
            Почта или пароль не подходят.
          </Notice>
        </Panel>

        <Panel title="Список с поиском и аккордеон">
          <Field label="Часовой пояс" hint="единый список продукта, поиск сам при длинном списке">
            {(props) => (
              <Csel
                id={props.id}
                value={zone}
                onChange={setZone}
                options={[
                  { value: 'Europe/Moscow', label: 'Москва', hint: 'сейчас 12:00' },
                  { value: 'Asia/Dushanbe', label: 'Душанбе', hint: 'сейчас 14:00' },
                  { value: 'Asia/Tbilisi', label: 'Тбилиси', hint: 'сейчас 13:00' },
                ]}
              />
            )}
          </Field>
          <Accordion>
            <AccordionItem title="Что такое привязка?">
              <p className="text-foreground/80 pb-4 text-sm">
                Событие зрителя, которое запускает действие сцены.
              </p>
            </AccordionItem>
            <AccordionItem title="Что такое кулдаун?">
              <p className="text-foreground/80 pb-4 text-sm">
                Пауза после срабатывания, пока привязка не сработает снова.
              </p>
            </AccordionItem>
          </Accordion>
        </Panel>

        <Panel title="Диалоги и тосты">
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setModal(true)}>
              Открыть подтверждение
            </Button>
            <Button variant="secondary" onClick={() => setSheet(true)}>
              Открыть лист
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                toast.show({ tone: 'success', title: 'Сохранено', text: 'Применится сразу' })
              }
            >
              Показать тост
            </Button>
          </div>
          <p className="text-foreground/80 text-sm">
            Ссылка в тексте: <Link href="https://efira.live">efira.live</Link> и{' '}
            <Link href="https://efira.live" quiet>
              приглушённая
            </Link>
            .
          </p>
        </Panel>

        <Panel title="Иконки" subtitle="Fluent UI System Icons через Iconify">
          <div className="flex flex-wrap gap-3">
            {(
              [
                'home',
                'live',
                'games',
                'gift',
                'heart',
                'chat',
                'share',
                'binding',
                'sound',
                'layout',
                'settings',
                'person',
              ] as const
            ).map((name) => (
              <span key={name} className="flex flex-col items-center gap-1 text-xs">
                <Icon name={name} size="xl" />
                {name}
              </span>
            ))}
          </div>
        </Panel>
      </main>

      <Modal
        open={modal}
        onClose={() => setModal(false)}
        title="Перевыпустить ссылку?"
        actions={
          <>
            <Button variant="danger-solid" size="m" onClick={() => setModal(false)}>
              Перевыпустить
            </Button>
            <Button variant="secondary" size="m" onClick={() => setModal(false)}>
              Отмена
            </Button>
          </>
        }
      >
        Старая ссылка перестанет работать немедленно.
      </Modal>

      <Sheet open={sheet} onClose={() => setSheet(false)} title="Лист">
        <p className="text-foreground/80 text-sm">Нижний лист - drawer FlyonUI.</p>
      </Sheet>
    </div>
  );
}
